/**
 * ADR-0106 — Model detail page at /app/build/models/:id. Overview, drift banner,
 * champion metrics + feature importance, versions (with Promote — the
 * champion/challenger surface; the Details expander shows any version's
 * feature importances pre-promotion), evaluations (with a two-version compare), and
 * training history (polls while a run is active). Metrics render as CSS-grid
 * cells + <div>-width bars (no charting dep).
 */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { apiFetch } from "../lib/api.js";

type Family = "regression" | "classification" | "anomaly" | "clustering";

type Model = {
  id: string;
  name: string;
  slug: string;
  algorithm_family: Family;
  algorithm: string;
  target_column: string | null;
  feature_columns: string[];
  scope: string;
  source_operation_id: string | null;
  source_node_id: string | null;
  created_at: string;
  updated_at: string;
};
type VersionRow = {
  id: string;
  version_number: number;
  algorithm: string;
  feature_schema: Array<{ name: string; type: string }>;
  metrics: Record<string, unknown>;
  feature_importances: Array<{ feature: string; importance: number }>;
  training_row_count: number | null;
  is_current: boolean;
  trained_at: string;
};
type TrainingRow = {
  id: string;
  status: "queued" | "running" | "success" | "failed" | "cancelled";
  triggered_by_name?: string;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  duration_ms: number | null;
};
type EvaluationRow = {
  id: string;
  model_version_id: string;
  status: "running" | "success" | "failed" | "cancelled";
  triggered_by_name?: string;
  eval_dataset_row_count: number | null;
  metrics: Record<string, unknown> | null;
  started_at: string;
};
type DriftChange = { field: string; label: string; before: string; after: string };
type SpecDrift = { has_drift: boolean; changes?: DriftChange[]; reason?: string };

// ADR-0109 — accuracy drift (champion training metrics vs its latest eval).
type AccDriftChange = { metric: string; label: string; baseline: number; current: number; degradation_pct: number };
type AccuracyDrift = {
  has_drift: boolean;
  changes?: AccDriftChange[];
  reason?: string;
  eval_time?: string;
  eval_rows?: number | null;
  sustained?: boolean;
};

type Detail = { model: Model; versions: VersionRow[]; trainings: TrainingRow[]; spec_drift: SpecDrift; accuracy_drift?: AccuracyDrift; source_operation_name?: string | null };

const ACTIVE = new Set(["queued", "running"]);

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

// Metric keys to surface as cells, in order, per family (skip confusion_matrix / classes).
const METRIC_ORDER: Record<Family, string[]> = {
  classification: ["auc", "accuracy", "precision", "recall", "f1"],
  regression: ["rmse", "mae", "r2"],
  anomaly: ["flagged_rate", "mean_score", "p95_score"],
  clustering: ["n_clusters", "silhouette", "davies_bouldin"],
};
const METRIC_LABELS: Record<string, string> = {
  auc: "AUC", accuracy: "Accuracy", precision: "Precision", recall: "Recall", f1: "F1",
  rmse: "RMSE", mae: "MAE", r2: "R²",
  flagged_rate: "Flagged rate", mean_score: "Mean score", p95_score: "P95 score",
  n_clusters: "Clusters", silhouette: "Silhouette", davies_bouldin: "Davies–Bouldin",
};
// Error metrics where a DECREASE is an improvement. Everything else (R², AUC,
// accuracy, precision, recall, F1, silhouette) improves when it INCREASES.
const LOWER_IS_BETTER = new Set(["rmse", "mae", "mse", "davies_bouldin"]);

const UNSUPERVISED_FAMILIES: ReadonlySet<Family> = new Set<Family>(["anomaly", "clustering"]);

/** Whole numbers (e.g. cluster count) print without decimals; everything else to 3dp. */
function fmtMetric(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(3);
}

function MetricCells({ family, metrics }: { family: Family; metrics: Record<string, unknown> | null | undefined }) {
  if (!metrics) return <div className="muted">No metrics.</div>;
  const keys = METRIC_ORDER[family].filter((k) => typeof metrics[k] === "number");
  if (keys.length === 0) return <div className="muted">No metrics.</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 }}>
      {keys.map((k) => (
        <div key={k} className="card" style={{ padding: 12 }}>
          <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>{METRIC_LABELS[k] ?? k}</div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{fmtMetric(metrics[k] as number)}</div>
        </div>
      ))}
    </div>
  );
}

/** Per-segment raw-feature profile for clustering (from the train-time metrics). */
function SegmentProfile({ metrics }: { metrics: Record<string, unknown> | null | undefined }) {
  const profile = Array.isArray(metrics?.segment_profile)
    ? (metrics!.segment_profile as Array<{ cluster_id: number; rows: number; feature_means: Record<string, number> }>)
    : [];
  if (profile.length === 0) return null;
  const featureNames = Array.from(new Set(profile.flatMap((s) => Object.keys(s.feature_means ?? {}))));
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ fontWeight: 600, marginTop: 6 }}>Segments</div>
      <div className="tableWrap">
        <table className="table tableCompact">
          <thead><tr><th>Cluster</th><th>Rows</th>{featureNames.map((f) => (<th key={f} title={f}>{f}</th>))}</tr></thead>
          <tbody>
            {profile.map((s) => (
              <tr key={s.cluster_id}>
                <td>#{s.cluster_id}</td>
                <td>{s.rows.toLocaleString()}</td>
                {featureNames.map((f) => (
                  <td key={f} className="mono">{typeof s.feature_means?.[f] === "number" ? s.feature_means[f].toFixed(2) : "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ImportanceBars({ importances }: { importances: Array<{ feature: string; importance: number }> }) {
  if (!importances || importances.length === 0) return <div className="muted">No feature importances.</div>;
  const max = Math.max(...importances.map((i) => i.importance), 0.0001);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {importances.slice(0, 20).map((fi) => (
        <div key={fi.feature}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
            <span className="mono" title={fi.feature} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fi.feature}</span>
            <span className="muted">{(fi.importance * 100).toFixed(1)}%</span>
          </div>
          <div style={{ width: "100%", height: 10, background: "var(--op-port-tabular, #e2e8f0)", borderRadius: 5, overflow: "hidden" }}>
            <div style={{ width: `${(fi.importance / max) * 100}%`, height: "100%", background: "var(--op-node-model-border, #db2777)" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** The right−left delta for a metric, colored green/red by whether it's an
 *  improvement (direction depends on the metric). Shown in the compare table. */
function ChangeCell({ metricKey, left, right }: { metricKey: string; left: number | null; right: number | null }) {
  if (left == null || right == null) return <span className="muted">—</span>;
  const delta = right - left;
  if (Math.abs(delta) < 5e-4) return <span className="muted">~0</span>; // rounds to 0.000 at 3dp
  const better = LOWER_IS_BETTER.has(metricKey) ? delta < 0 : delta > 0;
  const pct = left !== 0 ? (delta / Math.abs(left)) * 100 : null;
  const deltaStr = (delta >= 0 ? "+" : "") + delta.toFixed(3);
  const pctStr = pct != null ? ` (${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%)` : "";
  return <span className={`pill ${better ? "pillOk" : "pillBad"}`}>{deltaStr}{pctStr}</span>;
}

export function ModelDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [evaluations, setEvaluations] = useState<EvaluationRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [leftVersion, setLeftVersion] = useState<number | null>(null);
  const [rightVersion, setRightVersion] = useState<number | null>(null);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const [d, e] = await Promise.all([
        apiFetch<Detail>(`/api/tenant/models/${encodeURIComponent(id)}`),
        apiFetch<{ evaluations: EvaluationRow[] }>(`/api/tenant/models/${encodeURIComponent(id)}/evaluations`).catch(() => ({ evaluations: [] })),
      ]);
      setDetail(d);
      setEvaluations(e.evaluations ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(true); }, [load]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // Poll while the latest training is active.
  useEffect(() => {
    const latest = detail?.trainings?.[0];
    if (latest && ACTIVE.has(latest.status)) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => { void load(false); }, 3000);
      }
    } else {
      stopPolling();
    }
    return () => { /* cleared on unmount below */ };
  }, [detail, load, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  // Default the compare selectors to champion vs the most-recent other version.
  useEffect(() => {
    if (!detail) return;
    const champ = detail.versions.find((v) => v.is_current) ?? detail.versions[0];
    const other = detail.versions.find((v) => v.version_number !== champ?.version_number) ?? null;
    setLeftVersion((prev) => prev ?? champ?.version_number ?? null);
    setRightVersion((prev) => prev ?? other?.version_number ?? champ?.version_number ?? null);
  }, [detail]);

  async function onDelete() {
    if (!detail) return;
    const ok = await confirm.confirm({
      title: "Delete this model?",
      detail: `This deletes "${detail.model.name}", all its versions, and the trained artifacts. This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/models/${encodeURIComponent(id)}`, { method: "DELETE" });
      nav("/app/workspace/models");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function onRetrain() {
    if (busy) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/models/${encodeURIComponent(id)}/train`, { method: "POST", body: { trigger_type: "manual" } });
      toast.push({ kind: "success", message: "Training started" });
      await load(false);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function onPromote(versionId: string) {
    if (busy) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/models/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/promote`, { method: "POST" });
      toast.push({ kind: "success", message: "Promoted to champion" });
      await load(false);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function onDeleteVersion(versionId: string, versionNumber: number) {
    if (busy) return;
    const ok = await confirm.confirm({
      title: `Delete v${versionNumber}?`,
      detail: "This removes the version and its trained artifact. This cannot be undone. The champion can't be deleted — promote another version first.",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/models/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`, { method: "DELETE" });
      toast.push({ kind: "success", message: `Deleted v${versionNumber}` });
      await load(false);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <PageLayout title="Models"><LoadingState /></PageLayout>;
  if (error) return <PageLayout title="Models"><ErrorState detail={error} /></PageLayout>;
  if (!detail) return <PageLayout title="Models"><div className="muted">Model not found.</div></PageLayout>;

  const { model, versions, trainings, spec_drift, accuracy_drift, source_operation_name } = detail;
  const family = model.algorithm_family;
  const isUnsupervised = UNSUPERVISED_FAMILIES.has(family);
  const champion = versions.find((v) => v.is_current) ?? null;
  const latestTraining = trainings[0] ?? null;
  const trainingActive = latestTraining ? ACTIVE.has(latestTraining.status) : false;

  const evalByVersion = new Map<number, EvaluationRow>();
  const versionNumById = new Map(versions.map((v) => [v.id, v.version_number]));
  for (const ev of evaluations) {
    if (ev.status !== "success") continue;
    const vn = versionNumById.get(ev.model_version_id);
    if (vn == null) continue;
    if (!evalByVersion.has(vn)) evalByVersion.set(vn, ev); // evaluations are newest-first
  }
  const leftEval = leftVersion != null ? evalByVersion.get(leftVersion) ?? null : null;
  const rightEval = rightVersion != null ? evalByVersion.get(rightVersion) ?? null : null;
  const compareKeys = METRIC_ORDER[family];

  return (
    <PageLayout
      title={
        <span>
          <Link to="/app/workspace/models" style={{ color: "inherit", textDecoration: "none", opacity: 0.6 }}>Models</Link>
          <span style={{ opacity: 0.4, margin: "0 6px" }}>/</span>
          <span>{model.name}</span>
        </span>
      }
      actions={
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn btnSm" type="button" onClick={() => void onRetrain()} disabled={busy || trainingActive}>Re-train</button>
          <button className="btn btnDanger" type="button" onClick={() => void onDelete()}>Delete</button>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {/* Overview */}
        <div className="card" style={{ display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 600 }}>Overview</div>
          <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 6, fontSize: 13 }}>
            <div className="muted">Family</div><div>{family.charAt(0).toUpperCase() + family.slice(1)}</div>
            <div className="muted">Algorithm</div><div>{model.algorithm}</div>
            <div className="muted">Target</div><div className="mono">{model.target_column ?? "—"}</div>
            <div className="muted">Features</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {model.feature_columns.length === 0 ? "—" : model.feature_columns.map((f) => (
                <span key={f} className="pill" title={f}>{f}</span>
              ))}
            </div>
            <div className="muted">Scope</div><div>{model.scope}</div>
            <div className="muted">Source</div>
            <div>{model.source_operation_id ? <Link className="linkBtn" to={`/app/build/operations/${encodeURIComponent(model.source_operation_id)}${model.source_node_id ? `?node=${encodeURIComponent(model.source_node_id)}` : ""}`}>{source_operation_name ?? "Open source operation"}</Link> : "—"}</div>
            <div className="muted">Created</div><div>{fmtTs(model.created_at)}</div>
            <div className="muted">Updated</div><div>{fmtTs(model.updated_at)}</div>
          </div>
        </div>

        {/* Drift banner */}
        {spec_drift?.has_drift && spec_drift.changes && spec_drift.changes.length > 0 && !trainingActive ? (
          <div className="card" style={{ borderColor: "var(--danger-text)" }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Config changed since last train</div>
            <div className="help" style={{ marginBottom: 10 }}>Re-train to apply these changes:</div>
            <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              {spec_drift.changes.map((c) => (
                <div key={c.field} style={{ display: "grid", gap: 2 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{c.label}</div>
                  <div className="mono" style={{ fontSize: 12 }}><span className="muted">{c.before}</span>{" → "}<span>{c.after}</span></div>
                </div>
              ))}
            </div>
            <button className="btn btnSm btnPrimary" type="button" onClick={() => void onRetrain()} disabled={busy}>Re-train</button>
          </div>
        ) : null}

        {/* Accuracy drift banner (ADR-0109) — champion's training quality vs its latest prediction-vs-actual eval */}
        {accuracy_drift?.has_drift && accuracy_drift.changes && accuracy_drift.changes.length > 0 && !trainingActive ? (
          <div className="card" style={{ borderColor: "var(--danger-text)" }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              {isUnsupervised ? "Model quality has dropped since training" : "Accuracy has dropped since training"}
            </div>
            <div className="help" style={{ marginBottom: 10 }}>
              Measured on the latest prediction-vs-actual evaluation{accuracy_drift.eval_time ? ` (${fmtTs(accuracy_drift.eval_time)})` : ""}
              {typeof accuracy_drift.eval_rows === "number" ? ` over ${accuracy_drift.eval_rows.toLocaleString()} scored rows` : ""}. Re-training may help.
            </div>
            <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
              {accuracy_drift.changes.map((c) => (
                <div key={c.metric} style={{ display: "grid", gap: 2 }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{c.label}</div>
                  <div className="mono" style={{ fontSize: 12 }}>
                    <span className="muted">{c.baseline}</span>{" → "}<span>{c.current}</span>{" "}
                    <span style={{ color: "var(--danger-text)" }}>(−{c.degradation_pct.toFixed(1)}%)</span>
                  </div>
                </div>
              ))}
            </div>
            <button className="btn btnSm btnPrimary" type="button" onClick={() => void onRetrain()} disabled={busy}>Re-train</button>
          </div>
        ) : null}

        {/* Champion metrics + importance */}
        <div className="card" style={{ display: "grid", gap: 10 }}>
          <div style={{ fontWeight: 600 }}>Champion metrics{champion ? ` · v${champion.version_number}` : ""}</div>
          {champion ? (
            <>
              <MetricCells family={family} metrics={champion.metrics} />
              {isUnsupervised ? (
                <div className="help">Quality, not correctness — these measure {family === "clustering" ? "how cleanly the segments separate" : "how strongly rows deviate"}, not accuracy against a known answer.</div>
              ) : null}
              {family === "clustering" ? <SegmentProfile metrics={champion.metrics} /> : null}
              {!isUnsupervised ? (
                <>
                  <div style={{ fontWeight: 600, marginTop: 6 }}>Feature importance</div>
                  <ImportanceBars importances={champion.feature_importances} />
                </>
              ) : null}
            </>
          ) : (
            <div className="muted">No trained version yet.</div>
          )}
        </div>

        {/* Versions */}
        <div className="card" style={{ display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 600 }}>Versions</div>
          {versions.length === 0 ? (
            <div className="muted">No versions yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Version</th><th>Trained</th><th>Rows</th>
                    {METRIC_ORDER[family].map((k) => (<th key={k}>{METRIC_LABELS[k] ?? k}</th>))}
                    <th></th>
                    <th style={{ width: 220 }} />
                  </tr>
                </thead>
                <tbody>
                  {versions.map((v) => {
                    const expanded = expandedVersion === v.id;
                    return (
                      <Fragment key={v.id}>
                        <tr>
                          <td>v{v.version_number}</td>
                          <td className="muted">{fmtTs(v.trained_at)}</td>
                          <td>{v.training_row_count != null ? v.training_row_count.toLocaleString() : "—"}</td>
                          {METRIC_ORDER[family].map((k) => (
                            <td key={k}>{typeof v.metrics[k] === "number" ? fmtMetric(v.metrics[k] as number) : "—"}</td>
                          ))}
                          <td>{v.is_current ? <span className="pill pillOk">Champion</span> : null}</td>
                          <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <button className="btn btnSm" type="button" onClick={() => setExpandedVersion((p) => (p === v.id ? null : v.id))}>
                              {expanded ? "Hide" : "Details"}
                            </button>{" "}
                            {!v.is_current ? (
                              <>
                                <button className="btn btnSm" type="button" onClick={() => void onPromote(v.id)} disabled={busy}>Promote</button>{" "}
                                <button className="btn btnSm btnDanger" type="button" onClick={() => void onDeleteVersion(v.id, v.version_number)} disabled={busy}>Delete</button>
                              </>
                            ) : null}
                          </td>
                        </tr>
                        {expanded ? (
                          <tr>
                            <td colSpan={5 + METRIC_ORDER[family].length} style={{ background: "var(--surface-muted, #f8fafc)" }}>
                              <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 6, fontSize: 13, padding: "6px 4px" }}>
                                <div className="muted">Algorithm</div><div>{v.algorithm}</div>
                                <div className="muted">Family</div><div>{family.charAt(0).toUpperCase() + family.slice(1)}</div>
                                <div className="muted">Target</div><div className="mono">{model.target_column ?? "—"}</div>
                                <div className="muted">Features</div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                  {v.feature_schema && v.feature_schema.length > 0
                                    ? v.feature_schema.map((f) => (<span key={f.name} className="pill" title={`${f.name} (${f.type})`}>{f.name}</span>))
                                    : "—"}
                                </div>
                                {!isUnsupervised ? (
                                  <>
                                    <div className="muted">Importance</div>
                                    <div><ImportanceBars importances={v.feature_importances} /></div>
                                  </>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Evaluations + compare */}
        <div className="card" style={{ display: "grid", gap: 10 }}>
          <div style={{ fontWeight: 600 }}>Evaluations</div>
          {evaluations.length === 0 ? (
            <div className="muted">No evaluations yet. Add a Model Evaluate node pointed at a labelled dataset and run it.</div>
          ) : (
            <>
              {/* Two-version compare */}
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <select className="select" value={leftVersion ?? ""} onChange={(e) => setLeftVersion(e.target.value ? Number(e.target.value) : null)}>
                  {versions.map((v) => (<option key={v.id} value={v.version_number}>v{v.version_number}{v.is_current ? " (champion)" : ""}</option>))}
                </select>
                <span className="muted">vs</span>
                <select className="select" value={rightVersion ?? ""} onChange={(e) => setRightVersion(e.target.value ? Number(e.target.value) : null)}>
                  {versions.map((v) => (<option key={v.id} value={v.version_number}>v{v.version_number}{v.is_current ? " (champion)" : ""}</option>))}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1.4fr", gap: 6, fontSize: 13 }}>
                <div className="muted" style={{ fontWeight: 600 }} />
                <div className="muted" style={{ fontWeight: 600 }}>{leftVersion != null ? `v${leftVersion}` : "—"}</div>
                <div className="muted" style={{ fontWeight: 600 }}>{rightVersion != null ? `v${rightVersion}` : "—"}</div>
                <div className="muted" style={{ fontWeight: 600 }}>Change</div>

                <div className="muted">Eval dataset rows</div>
                <div>{leftEval?.eval_dataset_row_count?.toLocaleString() ?? "—"}</div>
                <div>{rightEval?.eval_dataset_row_count?.toLocaleString() ?? "—"}</div>
                <div>
                  {leftEval?.eval_dataset_row_count != null &&
                  rightEval?.eval_dataset_row_count != null &&
                  leftEval.eval_dataset_row_count !== rightEval.eval_dataset_row_count ? (
                    <span className="pill pillWarn" title="The two versions were evaluated on different-sized datasets — the comparison isn't apples-to-apples.">different sets</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </div>

                {compareKeys.map((k) => {
                  const lv = typeof leftEval?.metrics?.[k] === "number" ? (leftEval!.metrics![k] as number) : null;
                  const rv = typeof rightEval?.metrics?.[k] === "number" ? (rightEval!.metrics![k] as number) : null;
                  return (
                    <Fragment key={k}>
                      <div className="muted">{METRIC_LABELS[k] ?? k}</div>
                      <div>{lv != null ? lv.toFixed(3) : "—"}</div>
                      <div>{rv != null ? rv.toFixed(3) : "—"}</div>
                      <div><ChangeCell metricKey={k} left={lv} right={rv} /></div>
                    </Fragment>
                  );
                })}
              </div>
              <div className="help">Change = v{rightVersion ?? "?"} − v{leftVersion ?? "?"}; <span className="pill pillOk">green</span> = improvement (lower error / higher R²), <span className="pill pillBad">red</span> = regression. Compare two versions on the same labelled "gold" eval set — per-run holdout metrics on the Versions table use each run's own random split and aren't directly comparable.</div>
            </>
          )}
        </div>

        {/* Training history */}
        <div className="card" style={{ display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 600 }}>Training history</div>
          {trainings.length === 0 ? (
            <div className="muted">No training runs yet.</div>
          ) : (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr><th>Status</th><th>By</th><th>Started</th><th>Finished</th><th>Duration</th></tr>
                </thead>
                <tbody>
                  {trainings.map((t) => (
                    <tr key={t.id}>
                      <td>
                        {t.status === "success" ? <span className="pill pillOk">success</span>
                          : t.status === "failed" ? <span className="pill pillBad">failed</span>
                          : t.status === "cancelled" ? <span className="pill">cancelled</span>
                          : <span className="pill pillWarn">{t.status}</span>}
                        {t.status === "failed" && t.error_message ? <div className="help" style={{ color: "var(--danger-text)" }}>{t.error_message}</div> : null}
                      </td>
                      <td>{t.triggered_by_name ?? "—"}</td>
                      <td className="muted">{fmtTs(t.created_at)}</td>
                      <td className="muted">{fmtTs(t.completed_at)}</td>
                      <td className="muted">{t.duration_ms != null ? `${(t.duration_ms / 1000).toFixed(1)}s` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
